import useFormatter from "@/hooks/use-formatter.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { useCalcApplyTo } from "@/queries/use-rules.query.ts";
import { EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { Rule } from "@/types/rule.types.ts";
import { Box, Stack, Text, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface RuleEmployeeCountPreviewProps {
  rule: Rule;
  enabled?: boolean;
}

export function RuleEmployeeCountPreview({ rule, enabled = true }: RuleEmployeeCountPreviewProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderEmployeeName } = useFormatter();

  const { data: applyTo } = useCalcApplyTo({ rules: [rule] }, enabled);
  const employeeIds = applyTo?.employee_ids ?? [];
  const total = employeeIds.length;

  const filter = { ...EmployeeFilterDefault, employee_ids: employeeIds };
  const { data: employees } = useEmployees(filter, enabled && total > 0);

  if (!enabled || total === 0) return null;

  const names = (employees?.items ?? []).slice(0, 3).map(renderEmployeeName);
  const othersCount = total - names.length;
  const namesLine =
    othersCount > 0
      ? t("core:rule_names_and_others", { names: names.join(", "), count: othersCount })
      : names.join(", ");

  return (
    <Box className={"rounded-xs bg-dark/5 px-4 py-2"}>
      <Stack direction={"horizontal"} items={"center"} justify={"between"} gap={"sm"}>
        <Stack>
          <Text className={"text-title"}>{t("core:rule_total_employees", { count: total })}</Text>
          {namesLine && <Text className={"text-secondary"}>{namesLine}</Text>}
        </Stack>
        <Text
          className={"underline text-dark/95 cursor-pointer whitespace-nowrap hover:no-underline"}
          tabIndex={0}
          onClick={() => {
            openModal(PreviewEmployeesModal, { filter, addColumns: [] });
          }}
        >
          {t("common:view_all")}
        </Text>
      </Stack>
    </Box>
  );
}
